// WorkflowExecutionEngine.js - Execute visual workflows based on events
import eventBus, { EventTypes } from './EventBus'
import toast from 'react-hot-toast'
import { getFunctions, httpsCallable } from 'firebase/functions'
import workflowService from './workflowService'

class WorkflowExecutionEngine {
  constructor() {
    this.activeWorkflows = []
    this.executionHistory = []
    this.isInitialized = false
    this.namespace = 'workflow-engine' // Unique namespace for our listeners
    this.registeredListeners = [] // Track our listener IDs
    
    console.log('⚙️ Workflow Execution Engine initialized')
  }

  /**
   * Initialize engine and load active workflows
   * @param {string} tenantId - Optional tenant ID for loading from Firebase
   */
  async initialize(tenantId = null) {
    if (this.isInitialized) return

    console.log('🚀 Initializing Workflow Execution Engine...')

    // Clear only OUR listeners before re-registering (prevents duplicates)
    eventBus.clearNamespace(this.namespace)
    this.registeredListeners = []

    // Load workflows (Firebase first, then localStorage fallback)
    const workflows = await this.loadWorkflows(tenantId)
    this.activeWorkflows = workflows.filter(w => w.status === 'active' || !w.status)

    // Register event listeners for each workflow
    this.activeWorkflows.forEach(workflow => {
      this.registerWorkflowTriggers(workflow)
    })

    this.isInitialized = true
    console.log(`✅ Engine initialized with ${this.activeWorkflows.length} active workflows`)
  }

  /**
   * Load workflows from Firebase (master) and sync to localStorage (cache)
   * @param {string} tenantId - Tenant ID for loading from Firebase
   * @returns {Promise<Array>} Array of workflows
   */
  async loadWorkflows(tenantId = null) {
    try {
      // Try localStorage first for immediate availability
      let localWorkflows = JSON.parse(localStorage.getItem('visual_workflows') || '[]')
      
      // If we have a tenantId, sync from Firebase (source of truth)
      if (tenantId) {
        console.log(`🔄 Syncing workflows from Firebase for tenant: ${tenantId}`)
        const result = await workflowService.getWorkflows(tenantId)
        
        if (result.success && result.data && result.data.length > 0) {
          // Firebase has data - use it as master copy
          localWorkflows = result.data
          localStorage.setItem('visual_workflows', JSON.stringify(localWorkflows))
          console.log(`✅ Synced ${localWorkflows.length} workflows from Firebase to localStorage`)
        } else if (localWorkflows.length > 0) {
          // Firebase empty but localStorage has data - keep localStorage
          console.log(`⚠️ Firebase empty, using ${localWorkflows.length} workflows from localStorage`)
        } else {
          // Both empty
          console.log('ℹ️ No workflows found in Firebase or localStorage')
        }
      } else {
        // No tenantId - use localStorage only
        console.log(`📦 Loaded ${localWorkflows.length} workflows from localStorage (no tenantId provided)`)
      }

      return localWorkflows
    } catch (error) {
      console.error('❌ Error loading workflows:', error)
      // Fallback to localStorage on error
      return JSON.parse(localStorage.getItem('visual_workflows') || '[]')
    }
  }

  /**
   * Register event listeners for a workflow's triggers
   * @param {object} workflow - Workflow definition
   */
  registerWorkflowTriggers(workflow) {
    if (!workflow.nodes || workflow.nodes.length === 0) return

    // Find trigger nodes
    const triggerNodes = workflow.nodes.filter(node => node.type === 'triggerNode')

    triggerNodes.forEach(triggerNode => {
      const triggerType = triggerNode.data?.config?.triggerType || triggerNode.data?.triggerType

      if (triggerType) {
        // Map trigger type to event type
        const eventType = this.mapTriggerToEvent(triggerType)

        // Register with our namespace to prevent conflicts
        const unsubscribe = eventBus.on(eventType, async (eventData) => {
          console.log(`🎯 Workflow triggered: ${workflow.name}`, eventData)
          await this.executeWorkflow(workflow, triggerNode, eventData)
        }, {
          namespace: this.namespace // Critical: use our namespace
        })

        // Track this listener for diagnostics
        this.registeredListeners.push({
          workflowId: workflow.id,
          workflowName: workflow.name,
          eventType,
          triggerType,
          unsubscribe
        })

        console.log(`📌 Registered trigger: ${triggerType} for workflow: ${workflow.name} (namespace: ${this.namespace})`)
      }
    })
  }

  /**
   * Map trigger type to event type
   * @param {string} triggerType - Trigger type from workflow
   * @returns {string} Event type
   */
  mapTriggerToEvent(triggerType) {
    const mapping = {
      lead_added: EventTypes.LEAD_ADDED,
      email_opened: EventTypes.EMAIL_OPENED,
      email_clicked: EventTypes.EMAIL_CLICKED,
      email_replied: EventTypes.EMAIL_REPLIED,
      form_submitted: EventTypes.FORM_SUBMITTED,
      tag_added: EventTypes.CONTACT_TAGGED,
      deal_created: EventTypes.DEAL_CREATED,
      deal_stage_changed: EventTypes.DEAL_STAGE_CHANGED,
      deal_won: EventTypes.DEAL_WON,
      deal_lost: EventTypes.DEAL_LOST,
      deal_stuck: EventTypes.DEAL_STUCK_IN_STAGE,
      appointment_booked: EventTypes.APPOINTMENT_BOOKED
    }

    return mapping[triggerType] || triggerType
  }

  /**
   * Execute a workflow
   * @param {object} workflow - Workflow definition
   * @param {object} startNode - Starting trigger node
   * @param {object} eventData - Data from triggering event
   */
  async executeWorkflow(workflow, startNode, eventData) {
    const executionId = `exec_${Date.now()}_${Math.random()}`
    
    const execution = {
      id: executionId,
      workflowId: workflow.id,
      workflowName: workflow.name,
      startedAt: new Date().toISOString(),
      status: 'running',
      context: { ...eventData },
      steps: []
    }

    console.log(`▶️ Starting workflow execution: ${executionId}`)

    try {
      // Find edges connected to start node
      const edges = workflow.edges || []
      const nextNodes = this.getNextNodes(startNode.id, edges, workflow.nodes)

      // Execute each connected node sequentially
      for (const node of nextNodes) {
        await this.executeNode(node, execution, edges, workflow.nodes)
      }

      execution.status = 'completed'
      execution.completedAt = new Date().toISOString()
      
      console.log(`✅ Workflow completed: ${workflow.name}`)
      toast.success(`Workflow "${workflow.name}" executed successfully`)

    } catch (error) {
      execution.status = 'failed'
      execution.error = error.message
      execution.completedAt = new Date().toISOString()

      console.error(`❌ Workflow failed: ${workflow.name}`, error)
      toast.error(`Workflow "${workflow.name}" failed: ${error.message}`)
    }

    // Store execution history
    this.executionHistory.push(execution)
    if (this.executionHistory.length > 50) {
      this.executionHistory.shift() // Keep only last 50 executions
    }

    // Save to localStorage
    const execHistory = JSON.parse(localStorage.getItem('workflow_execution_history') || '[]')
    execHistory.push(execution)
    if (execHistory.length > 50) execHistory.shift()
    localStorage.setItem('workflow_execution_history', JSON.stringify(execHistory))
  }

  /**
   * Execute a single node
   * @param {object} node - Node to execute
   * @param {object} execution - Execution context
   * @param {array} edges - All workflow edges
   * @param {array} nodes - All workflow nodes
   */
  async executeNode(node, execution, edges, nodes) {
    const step = {
      nodeId: node.id,
      nodeType: node.type,
      nodeLabel: node.data?.label,
      startedAt: new Date().toISOString(),
      status: 'running'
    }

    execution.steps.push(step)

    console.log(`🔄 Executing node: ${node.data?.label || node.type}`)

    try {
      switch (node.type) {
        case 'actionNode':
          await this.executeAction(node, execution)
          break
        
        case 'conditionNode':
          const result = await this.evaluateCondition(node, execution)
          step.conditionResult = result
          // Find appropriate path based on condition
          const targetHandleId = result ? 'true' : 'false'
          const nextNodes = this.getNextNodes(node.id, edges, nodes, targetHandleId)
          for (const nextNode of nextNodes) {
            await this.executeNode(nextNode, execution, edges, nodes)
          }
          break
        
        case 'delayNode':
          await this.executeDelay(node, execution)
          break
        
        case 'dataNode':
          await this.executeDataOperation(node, execution)
          break
        
        case 'endNode':
          console.log('🛑 Workflow end node reached')
          step.status = 'completed'
          return // Stop execution
        
        default:
          console.log(`⚠️ Unknown node type: ${node.type}`)
      }

      step.status = 'completed'
      step.completedAt = new Date().toISOString()

      // Get next nodes and execute
      if (node.type !== 'conditionNode') { // Condition node handles its own next nodes
        const nextNodes = this.getNextNodes(node.id, edges, nodes)
        for (const nextNode of nextNodes) {
          await this.executeNode(nextNode, execution, edges, nodes)
        }
      }

    } catch (error) {
      step.status = 'failed'
      step.error = error.message
      step.completedAt = new Date().toISOString()
      throw error
    }
  }

  /**
   * Execute an action node
   * @param {object} node - Action node
   * @param {object} execution - Execution context
   */
  async executeAction(node, execution) {
    const config = node.data?.config || {}
    const actionType = config.actionType || node.data?.actionType

    console.log(`⚡ Executing action: ${actionType}`)

    switch (actionType) {
      case 'send_email':
        // Check if using campaign enrollment mode or direct template
        if (config.emailMode === 'campaign' && config.campaignId) {
          // Campaign enrollment mode - add lead to active campaign
          console.log('🚀 Enrolling lead in campaign:', config.campaignName)
          
          try {
            // Load campaigns from localStorage
            const campaigns = JSON.parse(localStorage.getItem('campaigns') || '[]')
            const campaignIndex = campaigns.findIndex(c => c.id === config.campaignId)
            
            if (campaignIndex >= 0) {
              const campaign = campaigns[campaignIndex]
              
              // Check if lead is already in campaign
              const leadContact = {
                id: execution.context.id || `contact_${Date.now()}`,
                email: execution.context.email,
                name: execution.context.name || `${execution.context.firstName || ''} ${execution.context.lastName || ''}`.trim(),
                firstName: execution.context.firstName || '',
                lastName: execution.context.lastName || '',
                company: execution.context.companyName || execution.context.company || '',
                companyName: execution.context.companyName || execution.context.company || '',
                industry: execution.context.industry || '',
                leadScore: execution.context.leadScore || 50,
                source: execution.context.source || 'workflow',
                tags: execution.context.tags || [],
                addedAt: new Date().toISOString()
              }
              
              // Initialize contacts array if it doesn't exist
              if (!campaign.contacts) {
                campaign.contacts = []
              }
              
              // Check if contact already exists in campaign
              const existingIndex = campaign.contacts.findIndex(c => c.email === leadContact.email)
              
              if (existingIndex >= 0) {
                console.log('⚠️ Contact already enrolled in campaign')
                toast('Contact already in campaign', { icon: 'ℹ️' })
              } else {
                // Add contact to campaign
                campaign.contacts.push(leadContact)
                
                // Update campaign in localStorage
                campaigns[campaignIndex] = campaign
                localStorage.setItem('campaigns', JSON.stringify(campaigns))
                
                console.log(`✅ Lead enrolled in campaign: ${campaign.name}`)
                console.log(`📊 Campaign now has ${campaign.contacts.length} contacts`)
                
                // Check if email should be sent immediately
                const shouldSendNow = this.shouldSendEmailImmediately(campaign)
                
                if (shouldSendNow) {
                  console.log('📧 Campaign set to send immediately - sending email now')
                  toast.loading('Sending email immediately...')
                  
                  // Send email to this specific lead
                  await this.sendEmailToLead(campaign, leadContact)
                  
                  toast.success(`Email sent to ${leadContact.email}!`)
                } else {
                  console.log('⏰ Campaign has batch/schedule settings - email will be sent according to campaign schedule')
                  toast.success(`Lead enrolled in "${campaign.name}" - email will be sent according to campaign schedule`)
                }
                
                // Trigger event for campaign update
                eventBus.emit('campaign_updated', {
                  campaignId: campaign.id,
                  campaignName: campaign.name,
                  action: 'lead_enrolled',
                  leadEmail: leadContact.email,
                  emailSent: shouldSendNow
                })
              }
            } else {
              console.error('❌ Campaign not found:', config.campaignId)
              toast.error('Campaign not found')
            }
          } catch (error) {
            console.error('❌ Error enrolling lead in campaign:', error)
            toast.error('Failed to enroll lead in campaign')
            throw error
          }
        } else {
          // Original template mode - send single email
          console.log('📧 Sending email...', {
            template: config.emailTemplate,
            subject: config.emailSubject,
            to: execution.context.email
          })
          toast.success('Email sent!')
        }
        break

      case 'send_sms':
        console.log('📱 Sending SMS...', execution.context.phone)
        toast.success('SMS sent!')
        break

      case 'add_to_list':
        console.log('➕ Adding to list:', config.listName)
        toast.success(`Added to list: ${config.listName}`)
        break

      case 'validate_email':
        console.log('✅ Validating email:', execution.context.email)
        execution.context.emailValid = true
        toast.success('Email validated!')
        break

      case 'create_deal':
        console.log('🏢 Creating deal for:', execution.context)
        const dealId = `deal_${Date.now()}`
        execution.context.dealId = dealId
        toast.success('Deal created!')
        break

      case 'webhook':
        console.log('🔗 Calling webhook:', config.webhookUrl)
        // In real implementation: await fetch(config.webhookUrl, { method: 'POST', body: JSON.stringify(execution.context) })
        toast.success('Webhook called!')
        break

      default:
        console.log('⚠️ Unknown action type:', actionType)
    }
  }

  /**
   * Evaluate a condition node
   * @param {object} node - Condition node
   * @param {object} execution - Execution context
   * @returns {boolean} Condition result
   */
  async evaluateCondition(node, execution) {
    const config = node.data?.config || {}
    const field = config.conditionField
    const operator = config.operator
    const value = config.conditionValue

    console.log(`🔀 Evaluating condition: ${field} ${operator} ${value}`)

    // Get field value from context
    const fieldValue = execution.context[field]

    // Evaluate based on operator
    let result = false
    switch (operator) {
      case 'equals':
        result = String(fieldValue) === String(value)
        break
      case 'not_equals':
        result = String(fieldValue) !== String(value)
        break
      case 'contains':
        result = String(fieldValue).includes(String(value))
        break
      case 'greater_than':
        result = Number(fieldValue) > Number(value)
        break
      case 'less_than':
        result = Number(fieldValue) < Number(value)
        break
      default:
        result = false
    }

    console.log(`🎯 Condition result: ${result}`)
    return result
  }

  /**
   * Execute a delay node
   * @param {object} node - Delay node
   * @param {object} execution - Execution context
   */
  async executeDelay(node, execution) {
    const config = node.data?.config || {}
    const delayAmount = parseInt(config.delayAmount || 1)
    const delayUnit = config.delayUnit || 'minutes'

    // Convert to milliseconds
    let delayMs = delayAmount
    switch (delayUnit) {
      case 'minutes':
        delayMs = delayAmount * 60 * 1000
        break
      case 'hours':
        delayMs = delayAmount * 60 * 60 * 1000
        break
      case 'days':
        delayMs = delayAmount * 24 * 60 * 60 * 1000
        break
      case 'weeks':
        delayMs = delayAmount * 7 * 24 * 60 * 60 * 1000
        break
    }

    console.log(`⏰ Delaying for ${delayAmount} ${delayUnit} (${delayMs}ms)`)

    // For demo purposes, use a short delay (max 5 seconds)
    const actualDelay = Math.min(delayMs, 5000)
    
    await new Promise(resolve => setTimeout(resolve, actualDelay))
    
    console.log(`✅ Delay completed`)
    toast.success(`Waited ${delayAmount} ${delayUnit}`)
  }

  /**
   * Execute a data operation node
   * @param {object} node - Data node
   * @param {object} execution - Execution context
   */
  async executeDataOperation(node, execution) {
    const config = node.data?.config || {}
    const operation = config.dataOperation || node.data?.dataOperation

    console.log(`💾 Executing data operation: ${operation}`)

    switch (operation) {
      case 'add_tag':
        execution.context.tags = execution.context.tags || []
        execution.context.tags.push(config.tagValue)
        console.log('🏷️ Tag added:', config.tagValue)
        toast.success(`Tag added: ${config.tagValue}`)
        break

      case 'remove_tag':
        execution.context.tags = (execution.context.tags || []).filter(t => t !== config.tagValue)
        console.log('🗑️ Tag removed:', config.tagValue)
        toast.success(`Tag removed: ${config.tagValue}`)
        break

      case 'update_field':
        execution.context[config.fieldName] = config.fieldValue
        console.log(`📝 Field updated: ${config.fieldName} = ${config.fieldValue}`)
        toast.success(`Field "${config.fieldName}" updated`)
        break

      case 'update_score':
        const currentScore = execution.context.leadScore || 0
        const scoreChange = parseInt(config.scoreChange || 0)
        execution.context.leadScore = currentScore + scoreChange
        console.log(`📈 Score updated: ${currentScore} -> ${execution.context.leadScore}`)
        toast.success(`Lead score ${scoreChange > 0 ? '+' : ''}${scoreChange}`)
        break

      case 'save_to_db':
        console.log('💾 Saving to database:', execution.context)
        // In real implementation: await saveToFirestore(execution.context)
        toast.success('Data saved!')
        break

      default:
        console.log('⚠️ Unknown data operation:', operation)
    }
  }

  /**
   * Get nodes connected from a source node
   * @param {string} sourceNodeId - Source node ID
   * @param {array} edges - All edges
   * @param {array} nodes - All nodes
   * @param {string} sourceHandle - Specific source handle (for condition nodes)
   * @returns {array} Connected nodes
   */
  getNextNodes(sourceNodeId, edges, nodes, sourceHandle = null) {
    const connectedEdges = edges.filter(edge => {
      const matchesSource = edge.source === sourceNodeId
      const matchesHandle = sourceHandle ? edge.sourceHandle === sourceHandle : true
      return matchesSource && matchesHandle
    })

    const nextNodes = connectedEdges
      .map(edge => nodes.find(node => node.id === edge.target))
      .filter(node => node !== undefined)

    return nextNodes
  }

  /**
   * Reload workflows from storage
   */
  reloadWorkflows() {
    console.log('🔄 Reloading workflows...')
    this.isInitialized = false
    // FIXED: Only clear OUR namespace, not ALL listeners
    // This preserves LeadToContactSync, DealAutomationEngine, and other services
    this.initialize()
  }

  /**
   * Get execution history
   * @param {number} limit - Number of recent executions
   * @returns {array} Execution history
   */
  getExecutionHistory(limit = 20) {
    return this.executionHistory.slice(-limit)
  }

  /**
   * Get active workflows
   * @returns {array} Active workflows
   */
  getActiveWorkflows() {
    return this.activeWorkflows
  }

  /**
   * Check if campaign should send email immediately
   * @param {object} campaign - Campaign object
   * @returns {boolean} True if should send now
   */
  shouldSendEmailImmediately(campaign) {
    // If campaign has a scheduled send date in the future, don't send now
    if (campaign.sendDate) {
      const sendDateTime = new Date(campaign.sendDate)
      const now = new Date()
      if (sendDateTime > now) {
        console.log('📅 Campaign scheduled for future:', sendDateTime)
        return false
      }
    }

    // If campaign has batch settings with a specific time, respect it
    if (campaign.batchSize && campaign.batchSize > 1) {
      // Check if there's a last sent time and if we should wait for the next batch
      if (campaign.lastBatchSentAt) {
        const lastSent = new Date(campaign.lastBatchSentAt)
        const now = new Date()
        const hoursSinceLastBatch = (now - lastSent) / (1000 * 60 * 60)
        
        // If last batch was sent less than 23 hours ago, wait for next scheduled batch
        if (hoursSinceLastBatch < 23) {
          console.log('⏰ Waiting for next batch window (last batch was', Math.round(hoursSinceLastBatch), 'hours ago)')
          return false
        }
      }
    }

    // Default: Send immediately if no scheduling constraints
    console.log('✅ No scheduling constraints - sending immediately')
    return true
  }

  /**
   * Send email to a specific lead enrolled via workflow
   * @param {object} campaign - Campaign object
   * @param {object} lead - Lead contact object
   */
  async sendEmailToLead(campaign, lead) {
    try {
      console.log('📧 Sending campaign email to workflow-enrolled lead:', lead.email)
      
      // Get Firebase services (assuming they're available globally or via import)
      const { getAuth } = await import('firebase/auth')
      const auth = getAuth()
      const user = auth.currentUser
      
      if (!user) {
        throw new Error('User not authenticated')
      }

      // Get tenant ID from localStorage (matching the pattern in App.jsx)
      const tenant = JSON.parse(localStorage.getItem('tenant') || '{}')
      if (!tenant.id) {
        throw new Error('Tenant ID not found')
      }

      // Personalize email content
      let personalizedContent = campaign.emailContent || ''
      personalizedContent = personalizedContent.replace(/\{\{firstName\}\}/g, lead.firstName || lead.name?.split(' ')[0] || 'there')
      personalizedContent = personalizedContent.replace(/\{\{lastName\}\}/g, lead.lastName || lead.name?.split(' ')[1] || '')
      personalizedContent = personalizedContent.replace(/\{\{name\}\}/g, lead.name || 'there')
      personalizedContent = personalizedContent.replace(/\{\{company\}\}/g, lead.company || lead.companyName || 'your company')
      personalizedContent = personalizedContent.replace(/\{\{companyName\}\}/g, lead.company || lead.companyName || 'your company')

      // Add tracking pixel
      const trackingPixel = `<img src="https://us-central1-market-genie-f2d41.cloudfunctions.net/trackEmailOpen?campaignId=${campaign.id}&recipientEmail=${encodeURIComponent(lead.email)}&userId=${user.uid}" width="1" height="1" style="display:none;visibility:hidden;" alt="" />`
      
      if (personalizedContent.toLowerCase().includes('</body>')) {
        personalizedContent = personalizedContent.replace(/<\/body>/i, trackingPixel + '</body>')
      } else {
        personalizedContent = personalizedContent + trackingPixel
      }

      // Convert line breaks to HTML if manual content
      if (campaign.useManualContent) {
        personalizedContent = personalizedContent.replace(/\n/g, '<br>')
        personalizedContent = personalizedContent.replace(
          /(https?:\/\/[^\s<]+)/g, 
          '<a href="$1" style="color: #14b8a6; text-decoration: underline;">$1</a>'
        )
      }

      // Prepare email data
      const emailData = {
        to: lead.email,
        subject: campaign.subject || 'No Subject',
        content: personalizedContent
      }

      console.log('📤 Sending email via Firebase Function:', emailData.to)

      // Call Firebase Function for email sending (using same pattern as App.jsx)
      const sendEmailFunction = httpsCallable(getFunctions(), 'sendEmailViaZohoSMTP')
      const result = await sendEmailFunction({
        to: emailData.to,
        subject: emailData.subject,
        htmlContent: emailData.content,
        tenantId: tenant.id
      })

      if (result.data.success) {
        console.log('✅ Email sent successfully to', lead.email)
        
        // Update campaign's sent tracking
        const campaigns = JSON.parse(localStorage.getItem('campaigns') || '[]')
        const campaignIndex = campaigns.findIndex(c => c.id === campaign.id)
        
        if (campaignIndex >= 0) {
          if (!campaigns[campaignIndex].sentEmails) {
            campaigns[campaignIndex].sentEmails = []
          }
          if (!campaigns[campaignIndex].sentEmails.includes(lead.email.toLowerCase())) {
            campaigns[campaignIndex].sentEmails.push(lead.email.toLowerCase())
          }
          
          campaigns[campaignIndex].emailsSent = (campaigns[campaignIndex].emailsSent || 0) + 1
          campaigns[campaignIndex].lastSent = new Date().toISOString()
          
          localStorage.setItem('campaigns', JSON.stringify(campaigns))
          console.log('📊 Updated campaign tracking:', campaigns[campaignIndex].emailsSent, 'emails sent')
        }

        return { success: true }
      } else {
        throw new Error(result.data.error || 'Failed to send email')
      }

    } catch (error) {
      console.error('❌ Error sending email to lead:', error)
      toast.error(`Failed to send email: ${error.message}`)
      throw error
    }
  }
}

// Create singleton instance
const workflowEngine = new WorkflowExecutionEngine()

export default workflowEngine
